import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { containsProfanity } from "../helper/profanityFilter";

const TOTAL_SECONDS = 5 * 60;
const RADIUS = 80;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const CouponRedemptionPage = () => {
    const { id } = useParams();
    const { token } = useAuth();
    const [coupon, setCoupon] = useState(null);
    const [business, setBusiness] = useState(null);
    const [serverName, setServerName] = useState("");
    const [secondsLeft, setSecondsLeft] = useState(TOTAL_SECONDS);
    const [timerStarted, setTimerStarted] = useState(false);
    const [redemptionTime] = useState(new Date());
    const [redemptionError, setRedemptionError] = useState(null);
    const localToken = token || localStorage.getItem("token");

    useEffect(() => {
        fetch(`${process.env.REACT_APP_API_URL}/api/coupons/${id}`)
            .then((res) => res.json())
            .then((data) => {
                setCoupon(data.coupon);
                setBusiness(data.business);
            })
            .catch((err) => console.error("Failed to load coupon:", err));
    }, [id]);

    useEffect(() => {
        if (!timerStarted || secondsLeft <= 0) return;
        const interval = setInterval(() => setSecondsLeft((s) => s - 1), 1000);
        return () => clearInterval(interval);
    }, [timerStarted, secondsLeft]);

    const minutes = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
    const seconds = String(secondsLeft % 60).padStart(2, "0");

    const progress = secondsLeft / TOTAL_SECONDS;
    const dashOffset = CIRCUMFERENCE * (1 - progress);
    const isLow = secondsLeft <= 60;
    const arcColor = isLow ? "#e84d1c" : "#2575fc";

    const handleRedeem = async () => {
        if (containsProfanity(serverName)) {
            setRedemptionError("Server name contains inappropriate language.");
            return;
        }
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/coupon-redemptions`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localToken}`,
                },
                body: JSON.stringify({ coupon_id: coupon.id, server_name: serverName.trim() }),
            });
            const data = await res.json();
            if (!res.ok) {
                setRedemptionError(data.error || "Failed to redeem coupon.");
                return;
            }
        } catch (err) {
            console.error("[Redemption] Network error:", err);
        }
        setTimerStarted(true);
    };

    // Availability window check
    const now       = new Date();
    const startsAt  = coupon?.starts_at  ? new Date(coupon.starts_at)  : null;
    const expiresAt = coupon?.expires_at ? new Date(coupon.expires_at) : null;
    const notYetAvailable = startsAt  && now < startsAt;
    const isExpired       = expiresAt && now > expiresAt;
    const fmtDate = (d) => d ? d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "";

    // Tip dot follows the end of the arc
    const tipAngle = -Math.PI / 2 + progress * 2 * Math.PI;
    const tipX = 100 + RADIUS * Math.cos(tipAngle);
    const tipY = 100 + RADIUS * Math.sin(tipAngle);

    const formattedDate = redemptionTime.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
    });
    const formattedTime = redemptionTime.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    });

    return (
        <section className="py-80">
            <style>{`
                @keyframes pulse-dot {
                    0%, 100% { r: 6; opacity: 1; }
                    50% { r: 9; opacity: 0.7; }
                }
                .timer-dot { animation: pulse-dot 1.2s ease-in-out infinite; }
            `}</style>

            <div className="container container-lg">
                <div className="row justify-content-center">
                    <div className="col-lg-5 col-md-7">
                        <div
                            className="border border-gray-100 rounded-16 p-48 text-center"
                            style={{ boxShadow: "0 4px 32px rgba(0,0,0,0.06)" }}
                        >
                            {/* Coupon name */}
                            <h5 className="mb-8 text-heading">
                                {coupon?.title ?? "Loading..."}
                            </h5>

                            {/* Business location */}
                            <p className="text-gray-600 mb-8" style={{ fontSize: 15 }}>
                                <i className="ph ph-map-pin" style={{ marginRight: 6 }} />
                                {business
                                    ? `${business.business_name} — ${business.address}, ${business.city}`
                                    : "—"}
                            </p>

                            {/* Redemption timestamp */}
                            <p className="text-gray-400 mb-0" style={{ fontSize: 13 }}>
                                {formattedDate} &middot; {formattedTime}
                            </p>

                            <span className="d-block border-top border-gray-100 mt-32 mb-40" />

                            {/* Countdown ring */}
                            <div className="d-flex flex-column align-items-center mb-40">
                                <svg width="200" height="200" viewBox="0 0 200 200">
                                    {/* Track */}
                                    <circle
                                        cx="100"
                                        cy="100"
                                        r={RADIUS}
                                        fill="none"
                                        stroke="#f0f0f0"
                                        strokeWidth="10"
                                    />
                                    {/* Progress arc */}
                                    <circle
                                        cx="100"
                                        cy="100"
                                        r={RADIUS}
                                        fill="none"
                                        stroke={arcColor}
                                        strokeWidth="10"
                                        strokeLinecap="round"
                                        strokeDasharray={CIRCUMFERENCE}
                                        strokeDashoffset={dashOffset}
                                        transform="rotate(-90 100 100)"
                                        style={{
                                            transition: "stroke-dashoffset 1s linear, stroke 0.4s ease",
                                        }}
                                    />
                                    {/* Moving tip dot */}
                                    {secondsLeft > 0 && (
                                        <circle
                                            className="timer-dot"
                                            cx={tipX}
                                            cy={tipY}
                                            r="6"
                                            fill={arcColor}
                                            style={{ transition: "fill 0.4s ease" }}
                                        />
                                    )}
                                </svg>

                                <span
                                    className="fw-bold text-heading"
                                    style={{ fontSize: 32, letterSpacing: 3, marginTop: 16 }}
                                >
                                    {minutes}:{seconds}
                                </span>
                                <span className="text-gray-400 mt-4" style={{ fontSize: 13 }}>
                                    minutes remaining
                                </span>
                            </div>

                            {timerStarted ? (
                                <>
                                    <p className="fw-medium text-gray-900" style={{ fontSize: 15 }}>
                                        Show Coupon To Server: {serverName}
                                    </p>
                                    <Link
                                        to={`/coupon/${id}`}
                                        className="btn btn-outline-main rounded-8 py-12 px-32 mt-16 d-inline-flex align-items-center gap-8"
                                    >
                                        <i className="ph ph-arrow-left" /> Back to Coupon
                                    </Link>
                                </>
                            ) : notYetAvailable ? (
                                <div className="d-flex flex-column align-items-center gap-12">
                                    <i className="ph ph-clock" style={{ fontSize: 40, color: "#94a3b8" }} />
                                    <p className="fw-semibold text-heading mb-0" style={{ fontSize: 16 }}>
                                        Not Available Until {fmtDate(startsAt)}
                                    </p>
                                    <p className="text-gray-500 text-sm mb-0 text-center">
                                        This coupon becomes redeemable on <strong>{fmtDate(startsAt)}</strong>. Come back then to redeem it.
                                    </p>
                                    <Link to={`/coupon/${id}`} className="btn btn-outline-main rounded-8 py-12 px-32 mt-8 d-inline-flex align-items-center gap-8">
                                        <i className="ph ph-arrow-left" /> Back to Coupon
                                    </Link>
                                </div>
                            ) : isExpired ? (
                                <div className="d-flex flex-column align-items-center gap-12">
                                    <i className="ph ph-warning-circle" style={{ fontSize: 40, color: "#ef4444" }} />
                                    <p className="fw-semibold text-heading mb-0" style={{ fontSize: 16 }}>Coupon Expired</p>
                                    <p className="text-gray-500 text-sm mb-0 text-center">
                                        This coupon expired on {fmtDate(expiresAt)} and can no longer be redeemed.
                                    </p>
                                    <Link to={`/coupon/${id}`} className="btn btn-outline-main rounded-8 py-12 px-32 mt-8 d-inline-flex align-items-center gap-8">
                                        <i className="ph ph-arrow-left" /> Back to Coupon
                                    </Link>
                                </div>
                            ) : (
                                <>
                                    {/* Server Name input */}
                                    <div className="mb-24 text-start">
                                        <label
                                            className="fw-medium text-gray-900 mb-8 d-block"
                                            style={{ fontSize: 14 }}
                                        >
                                            Server Name
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control rounded-8 border border-gray-200"
                                            style={{ padding: "12px 16px", fontSize: 15 }}
                                            placeholder="Enter server name"
                                            value={serverName}
                                            onChange={(e) => setServerName(e.target.value)}
                                        />
                                    </div>

                                    {redemptionError && (
                                        <p className="text-danger text-sm mb-16">{redemptionError}</p>
                                    )}

                                    {/* Redeem button */}
                                    <button
                                        className="btn bg-main-800 text-white rounded-8 py-14 w-100"
                                        disabled={!serverName.trim() || secondsLeft <= 0 || !!redemptionError}
                                        style={{ opacity: !serverName.trim() || secondsLeft <= 0 || !!redemptionError ? 0.5 : 1 }}
                                        onClick={handleRedeem}
                                    >
                                        Redeem Coupon
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default CouponRedemptionPage;
